/** Small pieces shared by the market pages: stars, seller chip, listing tiles and rows, report dialog. */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { BADGE_LABEL, listingPath, MAX_PHOTOS, PAYMENT_LABEL, REPORT_REASONS, REPORT_REASON_LABEL, WEEKDAY_LABEL, type ListingCard, type MarketFinish, type MarketPaymentMethod, type MeetingSchedule, type ReportTarget, type SellerBadge, type SellerSummary } from "../../shared/market";
import { formatArs } from "../../shared/money";
import { rarityLabel } from "../../shared/rarity";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL, type Condition } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { marketApi } from "./api";
import { useMarketCart } from "./cart";
import { useMarketViewer } from "./viewer";

export function useArs() {
  const { locale } = useI18n();
  return (n: number) => formatArs(n, locale);
}

/** ★★★★☆ 4,6 (12) — read out as text for screen readers. */
export function Stars({ value, count, size = "md" }: { value: number | null; count?: number; size?: "sm" | "md" }) {
  const { t, locale } = useI18n();
  if (value === null || !count) return <span className="stars stars-none muted small">{t("mkt.noReviews")}</span>;
  const full = Math.round(value);
  const label = t("mkt.starsLabel", { value: value.toLocaleString(locale === "es" ? "es-AR" : "en-US", { maximumFractionDigits: 1 }), count });
  return (
    <span className={`stars stars-${size}`} aria-label={label} title={label}>
      <span aria-hidden="true" className="stars-icons">
        {"★".repeat(full)}
        <span className="stars-empty">{"★".repeat(5 - full)}</span>
      </span>
      <span aria-hidden="true" className="stars-num">
        {value.toLocaleString(locale === "es" ? "es-AR" : "en-US", { maximumFractionDigits: 1 })} ({count})
      </span>
    </span>
  );
}

export function Avatar({ url, name, size = 40 }: { url: string | null; name: string; size?: number }) {
  return url ? (
    <img className="mkt-avatar" src={url} alt="" width={size} height={size} />
  ) : (
    <span className="mkt-avatar mkt-avatar-initial" style={{ width: size, height: size }} aria-hidden="true">
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

export function SellerChip({ seller }: { seller: SellerSummary }) {
  const { t } = useI18n();
  return (
    <Link to={`/vendedores/${seller.handle}`} className="seller-chip">
      <Avatar url={seller.avatarUrl} name={seller.displayName} size={28} />
      <span className="seller-chip-text">
        <strong>{seller.displayName}</strong>
        <span className="muted small">
          <Stars value={seller.ratingAvg} count={seller.ratingCount} size="sm" /> · {t("mkt.salesCount", { count: seller.sales })}
          {seller.verified && <span className="seller-verified"> · 🪪 {t("mkt.verified")}</span>}
          {seller.isNew && <span className="seller-new"> · 🆕 {t("mkt.newSeller")}</span>}
          {seller.limited && <span className="seller-new"> · 🤝 {t("mkt.limitedSeller")}</span>}
        </span>
      </span>
    </Link>
  );
}

const PAYMENT_ICON: Record<MarketPaymentMethod, string> = { mercadopago: "💳", transfer: "🏦", crypto: "🪙", cash: "💵" };

/** How a seller takes payment, one chip per method. */
export function PaymentChips({ methods }: { methods: MarketPaymentMethod[] }) {
  const { t, locale } = useI18n();
  if (!methods.length) return <>—</>;
  return (
    <ul className="pay-chips" aria-label={t("mkt.payments")}>
      {methods.map((m) => (
        <li key={m} className="pay-chip">
          <span aria-hidden="true">{PAYMENT_ICON[m]}</span> {PAYMENT_LABEL[m][locale]}
        </li>
      ))}
    </ul>
  );
}

export function Badges({ badges }: { badges: SellerBadge[] }) {
  const { locale } = useI18n();
  return (
    <ul className="badge-list">
      {badges.map((b) => (
        <li key={b} className={`seller-badge seller-badge-${b}`}>
          <span aria-hidden="true">{BADGE_LABEL[b].icon}</span> {BADGE_LABEL[b][locale]}
        </li>
      ))}
    </ul>
  );
}

/** "Lun 9:00–18:00 · Mar a Jue 10:00–12:00 · Sáb 9:00–17:00": consecutive days with the same window together. */
export function scheduleText(s: MeetingSchedule, locale: "es" | "en"): string {
  const week = [1, 2, 3, 4, 5, 6, 0];
  const short = (d: number) => WEEKDAY_LABEL[d][locale].slice(0, 3);
  const groups: { days: number[]; from: string; to: string }[] = [];
  let prev = -1;
  for (const [i, day] of week.entries()) {
    const slot = s.slots.find((x) => x.day === day);
    if (!slot) continue;
    const last = groups[groups.length - 1];
    if (last && prev === i - 1 && last.from === slot.from && last.to === slot.to) last.days.push(day);
    else groups.push({ days: [day], from: slot.from, to: slot.to });
    prev = i;
  }
  const join = locale === "es" ? " a " : "–";
  return groups
    .map((g) => `${g.days.length > 1 ? `${short(g.days[0])}${join}${short(g.days[g.days.length - 1])}` : short(g.days[0])} ${g.from}–${g.to}`)
    .join(" · ");
}

/** "NM · Inglés · Reverse holo · PSA 9" */
export function cardSpecs(l: Pick<ListingCard, "condition" | "language" | "finish" | "grader" | "grade">, locale: "es" | "en"): string {
  return [
    CONDITION_LABEL[l.condition as Condition]?.[locale] ?? l.condition,
    LANGUAGE_LABEL[l.language]?.[locale] ?? l.language,
    l.finish !== "normal" ? FINISH_LABEL[l.finish][locale] : null,
    l.grader ? `${l.grader} ${l.grade}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Where a market card leads. Several offers of one card show what they share (the card, the cheapest
 * price), never one copy's photo or condition, and open the card's page with every offer.
 */
function listingTarget(listing: ListingCard, groupLink?: (l: ListingCard) => string) {
  const group = listing.offers && listing.offers.listings > 1 && groupLink ? listing.offers : null;
  return { group, href: group ? groupLink!(listing) : listingPath(listing) };
}

/** Where a card offered more than once leads in a grouped list: its page, at the sellers' offers. */
export const offersLink = (l: ListingCard) => `/producto/${l.productSlug}#ofertas`;

export function ListingTile({
  listing,
  showSeller = true,
  featured = false,
  groupLink,
  buy = true,
}: {
  listing: ListingCard;
  showSeller?: boolean;
  /** A cart button on the copy (not in one's own shop). */
  buy?: boolean;
  /** In the "Destacados" strip: always labelled. */
  featured?: boolean;
  /** The whole market's list: where a card offered more than once leads (its page, with the sellers' offers). */
  groupLink?: (l: ListingCard) => string;
}) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const { group, href } = listingTarget(listing, groupLink);
  const state = featured ? { src: "featured" } : undefined;
  return (
    <article className="product-card listing-tile">
      <Link to={href} state={state} className="product-card-media">
        {/* Always the catalog image; the seller's photos of the card are extra, on its page (📷). */}
        <img src={imageUrl(listing.catalogImage)} alt={listing.name} loading="lazy" width={400} height={400} />
        {listing.presale ? <span className="tag tag-presale">{t("product.presale")}</span> : !group && listing.grader && <span className="tag tag-graded">{`${listing.grader} ${listing.grade}`}</span>}
        {featured && <span className="tag tag-featured">⭐ {t("mkt.feature.label")}</span>}
      </Link>
      <div className="product-card-body">
        <h3 className="product-card-title">
          <Link to={href} state={state}>
            {listing.name}
          </Link>
        </h3>
        <p className="product-card-meta">
          {rarityLabel(listing.rarity, locale) && <span className="rarity-tag">{rarityLabel(listing.rarity, locale)}</span>}
          {!group && <span>{cardSpecs(listing, locale)}</span>}
          {!group && <SellerPhotos listing={listing} />}
        </p>
        {listing.setName && <p className="muted small listing-set">{listing.setName}{listing.cardNumber ? ` · #${listing.cardNumber}` : ""}</p>}
        <div className="price-row">
          <span className="price">{group ? t("mkt.fromPrice", { price: ars(listing.priceArs) }) : ars(listing.priceArs)}</span>
          {!group && listing.quantity > 1 && <span className="muted small">{t("mkt.available", { count: listing.quantity })}</span>}
          {!group && buy && <AddToCart listing={listing} />}
        </div>
        {group && group.sellers > 1 ? (
          <p className="muted small listing-sellers">{t("mkt.soldByMany", { count: group.sellers })}</p>
        ) : (
          showSeller && <SellerChip seller={listing.seller} />
        )}
      </div>
    </article>
  );
}

export function ListingGrid({ items, showSeller, featured, groupLink, buy }: { items: ListingCard[]; showSeller?: boolean; featured?: boolean; groupLink?: (l: ListingCard) => string; buy?: boolean }) {
  return (
    <div className="product-grid">
      {items.map((l) => (
        <ListingTile key={l.id} listing={l} showSeller={showSeller} featured={featured} groupLink={groupLink} buy={buy} />
      ))}
    </div>
  );
}

export type ListingView = "cards" | "list";
const VIEW_KEY = "pf-market-view";

/** Cards or a compact list: the buyer's choice, remembered on this browser. */
export function useListingView(): [ListingView, (view: ListingView) => void] {
  const [view, setView] = useState<ListingView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "cards";
    } catch {
      return "cards";
    }
  });
  const change = (next: ListingView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Storage blocked: the choice lasts while the page is open.
    }
  };
  return [view, change];
}

export function ViewToggle({ view, onChange }: { view: ListingView; onChange: (view: ListingView) => void }) {
  const { t } = useI18n();
  return (
    <div className="view-toggle" role="group" aria-label={t("mkt.view.label")}>
      <button type="button" aria-pressed={view === "cards"} aria-label={t("mkt.view.cards")} title={t("mkt.view.cards")} onClick={() => onChange("cards")}>
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="currentColor">
          <rect x="1" y="1" width="6" height="6" rx="1" />
          <rect x="9" y="1" width="6" height="6" rx="1" />
          <rect x="1" y="9" width="6" height="6" rx="1" />
          <rect x="9" y="9" width="6" height="6" rx="1" />
        </svg>
      </button>
      <button type="button" aria-pressed={view === "list"} aria-label={t("mkt.view.list")} title={t("mkt.view.list")} onClick={() => onChange("list")}>
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="currentColor">
          <rect x="1" y="2" width="3" height="3" rx=".5" />
          <rect x="6" y="2.5" width="9" height="2" rx="1" />
          <rect x="1" y="6.5" width="3" height="3" rx=".5" />
          <rect x="6" y="7" width="9" height="2" rx="1" />
          <rect x="1" y="11" width="3" height="3" rx=".5" />
          <rect x="6" y="11.5" width="9" height="2" rx="1" />
        </svg>
      </button>
    </div>
  );
}

const FINISH_SHORT: Record<Exclude<MarketFinish, "normal">, string> = { holo: "Holo", reverse: "Rev", first_edition: "1ª" };

/** A short code on screen ("NM", "JA"); the full name for screen readers and on hover. */
function Code({ code, label, className = "" }: { code: string; label: string; className?: string }) {
  return (
    <span className={`row-code ${className}`} title={label}>
      <span aria-hidden="true">{code}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** An emoji that says something: named for screen readers and on hover. */
function Icon({ icon, label }: { icon: string; label: string }) {
  return (
    <span role="img" aria-label={label} title={label} className="row-icon">
      {icon}
    </span>
  );
}

/** A number with its icon ("★ 4,8 (10)", "🛍️ 12"): read out whole, and shown on hover. */
function Stat({ icon, text, label }: { icon: string; text: string; label: string }) {
  return (
    <span role="img" aria-label={label} title={label} className="row-stat">
      <span aria-hidden="true">{icon}</span> {text}
    </span>
  );
}

/**
 * The market's list view (like Cardmarket's): one line per listing with a small picture, the card,
 * its condition, language and finish as codes, the seller's rating and sales as icons, and the price.
 */
export function ListingRows({ items, groupLink, featured = false, buy = true }: { items: ListingCard[]; groupLink?: (l: ListingCard) => string; /** The "Destacados" strip. */ featured?: boolean; /** Cart buttons (not in one's own shop). */ buy?: boolean }) {
  const { t } = useI18n();
  return (
    <ul className="listing-rows" role="list" aria-label={featured ? t("mkt.feature.stripTitle") : t("mkt.view.listLabel")}>
      {items.map((l) => (
        <ListingRow key={l.id} listing={l} groupLink={groupLink} featured={featured} buy={buy} />
      ))}
    </ul>
  );
}

function ListingRow({ listing: l, groupLink, featured, buy }: { listing: ListingCard; groupLink?: (l: ListingCard) => string; featured: boolean; buy: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const { group, href } = listingTarget(l, groupLink);
  const state = featured ? { src: "featured" } : undefined;
  const condition = CONDITION_LABEL[l.condition as Condition]?.[locale] ?? l.condition;
  const rarity = rarityLabel(l.rarity, locale);
  const s = l.seller;
  const num = (n: number) => n.toLocaleString(locale === "es" ? "es-AR" : "en-US", { maximumFractionDigits: 1 });
  return (
    <li className={group || !buy ? "listing-row" : "listing-row has-cart"}>
      <img className="listing-row-thumb" src={imageUrl(l.catalogImage)} alt={l.name} loading="lazy" width={36} height={50} />
      <div className="listing-row-main">
        <Link to={href} state={state} className="listing-row-name">
          {l.name}
        </Link>
        <span className="listing-row-specs small">
          {l.setName && (
            <span className="muted">
              {l.setName}
              {l.cardNumber ? ` · #${l.cardNumber}` : ""}
            </span>
          )}
          {rarity && <span className="rarity-tag">{rarity}</span>}
          {!group && <Code code={l.condition === "SEALED" ? condition : l.condition} label={condition} />}
          {!group && <Code code={l.language.toUpperCase()} label={LANGUAGE_LABEL[l.language]?.[locale] ?? l.language} />}
          {!group && l.finish !== "normal" && <Code code={FINISH_SHORT[l.finish]} label={FINISH_LABEL[l.finish][locale]} />}
          {!group && l.grader && <span className="row-code row-graded">{`${l.grader} ${l.grade}`}</span>}
          {l.presale && <span className="row-code row-presale">{t("product.presale")}</span>}
          {!group && <SellerPhotos listing={l} thumb />}
        </span>
      </div>
      <div className="listing-row-seller small">
        {group && group.sellers > 1 ? (
          <span className="muted">{t("mkt.soldByMany", { count: group.sellers })}</span>
        ) : (
          <Link to={`/vendedores/${s.handle}`} className="listing-row-seller-link">
            <strong>{s.displayName}</strong>
            {s.ratingAvg !== null && s.ratingCount > 0 ? (
              <Stat icon="★" text={`${num(s.ratingAvg)} (${s.ratingCount})`} label={t("mkt.starsLabel", { value: num(s.ratingAvg), count: s.ratingCount })} />
            ) : (
              <Stat icon="☆" text="–" label={t("mkt.noReviews")} />
            )}
            <Stat icon="🛍️" text={String(s.sales)} label={t("mkt.salesCount", { count: s.sales })} />
            {s.verified && <Icon icon="🪪" label={t("mkt.verified")} />}
            {s.isNew && <Icon icon="🆕" label={t("mkt.newSeller")} />}
            {s.limited && <Icon icon="🤝" label={t("mkt.limitedSeller")} />}
          </Link>
        )}
      </div>
      <div className="listing-row-price">
        <strong>{group ? t("mkt.fromPrice", { price: ars(l.priceArs) }) : ars(l.priceArs)}</strong>
        {!group && l.quantity > 1 && <span className="muted small">{t("mkt.available", { count: l.quantity })}</span>}
      </div>
      {!group && buy && <AddToCart listing={l} />}
    </li>
  );
}

/** A shop's preview in its editor: its owner sees the cart buttons a buyer gets (inert there). */
export const AsBuyer = createContext(false);

/** One copy to the seller's order right from a list, without opening the listing (the header's cart counts it). */
export function AddToCart({ listing: l }: { listing: ListingCard }) {
  const { t } = useI18n();
  const cart = useMarketCart();
  const viewer = useMarketViewer();
  const asBuyer = useContext(AsBuyer);
  const [added, setAdded] = useState(false);
  // A favorite can be sold out, paused or its shop away (the list's own cards are always live); nobody buys their own.
  const d = l as ListingCard & { status?: string; vacationUntil?: string | null };
  if (l.quantity < 1 || (d.status && d.status !== "active") || d.vacationUntil || (!asBuyer && viewer.isMine(l.seller.handle))) return null;
  const label = t("mkt.addToOrder", { seller: l.seller.displayName });
  return (
    <button
      type="button"
      className={added ? "cart-add is-added" : "cart-add"}
      aria-label={added ? t("mkt.addedToOrder") : label}
      title={label}
      onClick={() => {
        cart.add(l.seller.handle, { listingId: l.id, quantity: 1, max: l.quantity, name: l.name, priceArs: l.priceArs, photo: l.catalogImage });
        setAdded(true);
        window.setTimeout(() => setAdded(false), 1500);
      }}
    >
      {added ? (
        <span aria-hidden="true">✓</span>
      ) : (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="20" r="1.5" />
          <circle cx="18" cy="20" r="1.5" />
          <path d="M2 3h3l2.7 12.4a1 1 0 0 0 1 .8h9.6a1 1 0 0 0 1-.8L21 7H6.2" />
        </svg>
      )}
    </button>
  );
}

/**
 * 📷: the seller's photos of this copy (at most MAX_PHOTOS), in a gallery over the list. In a row
 * (`thumb`) the first photo itself opens it, as a small picture: easier to tap than the icon.
 */
export function SellerPhotos({ listing: l, thumb = false }: { listing: ListingCard; thumb?: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(0);
  const photos = (l.photos ?? (l.photo ? [l.photo] : [])).slice(0, MAX_PHOTOS);
  if (!l.ownPhotos || photos.length === 0) return null;
  const label = t("mkt.photos.open", { count: photos.length });
  return (
    <>
      <button type="button" className={thumb ? "photos-btn photos-thumb-btn" : "photos-btn"} aria-label={label} title={label} onClick={() => (setShown(0), setOpen(true))}>
        {thumb ? <img src={imageUrl(photos[0])} alt="" width={28} height={38} loading="lazy" /> : "📷"}
        {photos.length > 1 ? (thumb ? <span className="photos-count">{photos.length}</span> : ` ${photos.length}`) : ""}
      </button>
      {/* Over the page, not inside the card (its line is a <p>, and the card clips). */}
      {open &&
        createPortal(
        <Dialog title={t("mkt.photos.title", { name: l.name })} onClose={() => setOpen(false)} className="photos-dialog">
          <img className="photos-main" src={imageUrl(photos[shown])} alt={`${l.name} — ${t("mkt.sellerPhoto", { n: shown + 1 })}`} />
          {photos.length > 1 && (
            <div className="photos-thumbs">
              {photos.map((p, i) => (
                <button key={p} type="button" aria-pressed={i === shown} aria-label={t("mkt.sellerPhoto", { n: i + 1 })} onClick={() => setShown(i)}>
                  <img src={imageUrl(p)} alt="" width={64} height={64} />
                </button>
              ))}
            </div>
          )}
          <p className="muted small">{t("mkt.sellerPhotoNote")}</p>
          <div className="row-actions">
            <Link to={listingPath(l)} className="btn btn-outline btn-sm">
              {t("mkt.photos.listing")}
            </Link>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setOpen(false)}>
              {t("nav.close")}
            </button>
          </div>
        </Dialog>,
          document.body,
        )}
    </>
  );
}

/** Accessible modal dialog: focus inside, Escape and backdrop close it. */
export function Dialog({ title, onClose, children, className }: { title: string; onClose: () => void; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="evo-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`evo-modal mkt-dialog${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** "Denunciar": reason + details, sent to the shop's moderators. */
export function ReportButton({ target, targetId, label }: { target: ReportTarget; targetId: string; label?: string }) {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  if (!auth.user) return null;
  const submit = async () => {
    setState("sending");
    try {
      await marketApi.report(target, targetId, reason, details);
      setState("sent");
    } catch {
      setState("error");
    }
  };
  return (
    <>
      <button type="button" className="btn btn-link btn-sm report-btn" onClick={() => setOpen(true)}>
        🚩 {label ?? t("mkt.report")}
      </button>
      {/* Over the page (not in a row's menu or a card that would clip or cover it). */}
      {open &&
        createPortal(
          <Dialog title={t("mkt.reportTitle")} onClose={() => setOpen(false)}>
            {state === "sent" ? (
              <p>{t("mkt.reportSent")}</p>
            ) : (
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (reason) void submit();
                }}
              >
                <fieldset className="radio-list">
                  <legend>{t("mkt.reportReason")}</legend>
                  {REPORT_REASONS.map((r) => (
                    <label key={r}>
                      <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} /> {REPORT_REASON_LABEL[r][locale]}
                    </label>
                  ))}
                </fieldset>
                <div className="field">
                  <label htmlFor="report-details">{t("mkt.reportDetails")}</label>
                  <textarea id="report-details" rows={3} maxLength={2000} value={details} onChange={(e) => setDetails(e.target.value)} />
                </div>
                {state === "error" && <p className="form-error">{t("error.generic")}</p>}
                <button type="submit" className="btn btn-primary" disabled={!reason || state === "sending"}>
                  {t("mkt.reportSend")}
                </button>
              </form>
            )}
            <button type="button" className="btn btn-link" onClick={() => setOpen(false)}>
              {t("nav.close")}
            </button>
          </Dialog>,
          document.body,
        )}
    </>
  );
}

/** Copies a value (CBU, alias, wallet) with a short confirmation. */
export function CopyValue({ label, value, display, className }: { label: string; value: string; /** Shown instead of the copied value (an amount with its "$"). */ display?: string; className?: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <div className={className ? `copy-row ${className}` : "copy-row"}>
      <span className="muted small">{label}</span>
      <code>{display ?? value}</code>
      <button
        type="button"
        className="btn btn-link btn-sm"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? t("mkt.copied") : t("mkt.copy")}
      </button>
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const { t } = useI18n();
  return <span className={`pill pill-mkt-${status}`}>{t(`mkt.status.${status}` as never)}</span>;
}
